/**
 * TA-10618: layout math for the web DrawerHeader title row.
 *
 * The row is sized from two measured widths (the header shell and the
 * headerRight cluster). Two things made that measurement visible to users:
 *
 * 1. WebDrawerNavigator renders every route inside a ResourceSavingView, which
 *    hides blurred screens with `display: none`. RN-web reports that through
 *    onLayout as a 0-width layout, so a blurred header stored 0 and painted a
 *    collapsed title row on its next focus.
 * 2. Lazily mounted routes paint once before any measurement lands.
 *
 * The Global Search icon used to sit at the right edge of that row, so both
 * cases dropped it next to the page title for a frame. The icon now lives in
 * headerRight (flexbox-positioned, like New Task); these helpers keep the row
 * itself from painting a collapsed width, and are pure so the behaviour is
 * testable without mounting the whole web app shell.
 */

/**
 * A 0 (or negative) onLayout width means the header is hidden, not resized.
 */
export const isRealLayoutWidth = (layoutWidth: number): boolean =>
  layoutWidth > 0;

/**
 * Measured width to keep: a hidden header keeps its last real measurement
 * rather than collapsing to 0.
 */
export const nextMeasuredWidth = (
  previousWidth: number | undefined,
  layoutWidth: number
): number | undefined =>
  isRealLayoutWidth(layoutWidth) ? layoutWidth : previousWidth;

/**
 * Width of the header title row. Both measurements are needed before the row
 * can be sized: with only the shell measured the row would be one
 * headerRight-cluster too wide. Until then it sizes itself intrinsically.
 */
export const getTitleRowWidth = ({
  mainPanelWidth,
  rightPartWidth,
  gap,
}: {
  mainPanelWidth: number | undefined;
  rightPartWidth: number | undefined;
  gap: number;
}): number | '100%' =>
  mainPanelWidth === undefined || rightPartWidth === undefined
    ? '100%'
    : mainPanelWidth - rightPartWidth - gap;
